/**
 * CommonGroundPage — "Explore Alignment" → the Common Ground card.
 * Fuses the DOB behavioral read (Common Goal vs Common Enemy bias) with a
 * factual layer (LinkedIn / bio paste) to surface a real Common Goal + Common
 * Enemy and how to lead with the right lever. Terms from *Molecular Influence*.
 */

import { useState } from "react";
import { useSession } from "../lib/useSession.js";
import { Wordmark, SectionHeader, Callout, PrintToPdfButton } from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import { parseJsonResponse } from "../lib/api-fetch.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

export default function CommonGroundPage() {
  const { session } = useSession();

  const [mentions, setMentions] = useState([]);
  const [mentionText, setMentionText] = useState("");
  const [bio, setBio] = useState("");
  const [yourCompany, setYourCompany] = useState("");
  const [yourGoal, setYourGoal] = useState("");
  const [knownAdversaries, setKnownAdversaries] = useState("");

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);

  const generate = async () => {
    if (mentions.length < 1) {
      setError("Mention the person you want to align with (a saved Profile).");
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const res = await fetch("/.netlify/functions/common-ground", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          counterpart_profile_id: mentions[0].profile_id,
          factual_layer: { linkedin_or_bio_text: bio || null },
          your_side: {
            your_company_or_offer: yourCompany || null,
            your_goal: yourGoal || null,
            your_known_adversaries: knownAdversaries || null,
          },
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        setError(data.error || `Generation failed (HTTP ${res.status})`);
        setGenerating(false);
        return;
      }
      setCard(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to use Explore Alignment.</p>;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}><Wordmark scale={1.0} /></div>

      {!card ? (
        <div>
          <SectionHeader index={1} title="Explore Alignment" />

          <Callout.Tip>
            Find your fastest way in with one person — a real <strong>Common Goal</strong> to align on and a real <strong>Common Enemy</strong> to unite against, plus which lever to lead with given how they're wired. Paste their LinkedIn or bio for the sharpest read.
          </Callout.Tip>

          <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
            <Field label="Who are you aligning with? (mention with @)">
              <MentionAutocomplete
                value={mentionText}
                onChange={setMentionText}
                mentions={mentions}
                onMentionsChange={setMentions}
                placeholder="@Dana — a saved profile"
                rows={2}
                bearerToken={session.access_token}
              />
            </Field>

            <Field label="Their LinkedIn / bio (paste — optional but high-value)">
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={6} placeholder="Paste their LinkedIn About + experience, or any bio / background…" style={inputStyle} />
            </Field>

            <Field label="You / your company / what you offer (optional)">
              <input value={yourCompany} onChange={(e) => setYourCompany(e.target.value)} placeholder="What you represent" style={inputStyle} />
            </Field>

            <Field label="Your goal with them (optional)">
              <input value={yourGoal} onChange={(e) => setYourGoal(e.target.value)} placeholder="What you want from the relationship" style={inputStyle} />
            </Field>

            <Field label="A shared adversary you already know of (optional)">
              <input value={knownAdversaries} onChange={(e) => setKnownAdversaries(e.target.value)} placeholder="A competitor, market shift, or problem you both face" style={inputStyle} />
            </Field>

            {error && <ErrorBox error={error} />}

            <button
              onClick={generate}
              disabled={generating}
              style={generating ? terminalActionButtonDisabledStyle : terminalActionButtonStyle}
            >
              {generating ? "Finding common ground…" : "Explore Alignment"}
            </button>
          </div>
        </div>
      ) : (
        <article>
          <h1 style={titleStyle}>{card.title || "Common Ground"}</h1>
          {card.common_enemy_found === false && (
            <Callout.WatchOut>
              No genuine shared adversary surfaced from what you gave — lean on the Common Goal. (A real Common Enemy beats a manufactured one every time.)
            </Callout.WatchOut>
          )}

          <div style={{ display: "grid", gap: 10, margin: "18px 0 28px" }}>
            {(card.card_sections || []).map((c, i) => (
              <div key={i} style={cardStyle}>
                <div style={cardLabelStyle}>{c.label}</div>
                <div style={cardBodyStyle}>{c.body}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button onClick={() => setCard(null)} style={{ ...terminalActionButtonStyle, background: "transparent", color: "var(--ppr-color-accent-navy)" }}>
              ← New
            </button>
            <PrintToPdfButton label="Save as PDF" />
          </div>
        </article>
      )}
    </div>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
  resize: "vertical",
};

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-4xl)",
  color: "var(--ppr-color-accent-navy)",
  letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
  lineHeight: "var(--ppr-typography-line-height-tight)",
  margin: "0 0 8px",
};

const cardStyle = {
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  padding: "12px 14px",
};
const cardLabelStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-coral)",
  marginBottom: 4,
};
const cardBodyStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  color: "var(--ppr-color-text-primary)",
  lineHeight: 1.5,
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
    }}>
      {error}
    </div>
  );
}
